import React, { useState } from "react";
import { ISAD_CONFIG } from "../../models/isad";

const ANNOTATION_ICONS = {
  heavy_rain: "🌧️",
  road_works: "🚧",
  operator_change: "👤",
};

const LEVEL_ORDER = { A: 0, B: 1, C: 2, D: 3, E: 4 };

function matchAnnotations(campaigns, annotations) {
  const map = {};
  for (const ann of annotations) {
    const annTs = new Date(ann.date).getTime();
    let nearestId = null;
    let nearestDiff = Infinity;
    for (const c of campaigns) {
      const diff = Math.abs(new Date(c.date).getTime() - annTs);
      if (diff < nearestDiff) {
        nearestId = c.id;
        nearestDiff = diff;
      }
    }
    if (nearestId) {
      if (!map[nearestId]) map[nearestId] = [];
      map[nearestId].push(ann);
    }
  }
  return map;
}

function detectOscillation(campaigns) {
  for (let i = 1; i < campaigns.length - 1; i++) {
    const p = LEVEL_ORDER[campaigns[i - 1].fused_level] ?? 0;
    const c = LEVEL_ORDER[campaigns[i].fused_level] ?? 0;
    const n = LEVEL_ORDER[campaigns[i + 1].fused_level] ?? 0;
    if ((c > p && c > n) || (c < p && c < n)) {
      return { before: campaigns[i - 1], after: campaigns[i + 1] };
    }
  }
  return null;
}

const fmt = (dateStr, opts) =>
  new Date(dateStr).toLocaleDateString("en-GB", opts);

const CampaignCard = ({ camp, label }) => {
  const cfg = ISAD_CONFIG[camp.fused_level];
  return (
    <div className="flex-1 rounded-lg border border-gray-200 p-2.5 dark:border-white/10">
      <p className="mb-0.5 text-[9px] font-bold uppercase text-gray-400">
        {label}
      </p>
      <p className="mb-1.5 text-[10px] text-gray-500">
        {camp.id} · {fmt(camp.date, { month: "short", year: "numeric" })}
      </p>
      <div className="flex items-center gap-2">
        <div
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-xs font-bold text-white"
          style={{ background: cfg?.color ?? "#9e9e9e" }}
        >
          {camp.fused_level}
        </div>
        <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
          {cfg?.label ?? "No data"}
        </span>
      </div>
    </div>
  );
};

const WhatChanged = ({ campaigns, annotations }) => {
  const [activeDot, setActiveDot] = useState(null);
  const annMap = matchAnnotations(campaigns, annotations);
  const oscillation = detectOscillation(campaigns);
  const activeCamp = campaigns.find((c) => c.id === activeDot);

  if (campaigns.length === 0) {
    return (
      <section className="border-b border-gray-100 p-4 dark:border-white/5">
        <p className="mb-3 text-[10px] font-bold uppercase tracking-widest text-gray-400">
          What changed
        </p>
        <p className="text-xs text-gray-400">No campaign records available.</p>
      </section>
    );
  }

  return (
    <section className="border-b border-gray-100 p-4 dark:border-white/5">
      <p className="mb-3 text-[10px] font-bold uppercase tracking-widest text-gray-400">
        What changed
      </p>

      <div className="overflow-x-auto pb-1">
        <div
          className="flex min-w-0 items-end"
          style={{ minWidth: campaigns.length * 44 }}
        >
          {campaigns.map((camp, i) => {
            const cfg = ISAD_CONFIG[camp.fused_level];
            const color = cfg?.color ?? "#9e9e9e";
            const anns = annMap[camp.id] ?? [];
            const isActive = activeDot === camp.id;
            const isLast = i === campaigns.length - 1;

            return (
              <div key={camp.id} className="flex flex-1 flex-col items-center">
                <div className="mb-1 flex gap-0.5" style={{ minHeight: 20 }}>
                  {anns.map((ann, j) => (
                    <span
                      key={j}
                      title={ann.label}
                      aria-label={ann.label}
                      className="text-sm leading-none"
                    >
                      {ANNOTATION_ICONS[ann.type] ?? "📌"}
                    </span>
                  ))}
                </div>

                <div className="relative flex w-full items-center">
                  {i > 0 && (
                    <div className="h-px flex-1 bg-gray-200 dark:bg-white/10" />
                  )}
                  <button
                    onClick={() => setActiveDot(isActive ? null : camp.id)}
                    aria-label={`Campaign ${camp.id}: ISAD ${camp.fused_level}`}
                    aria-pressed={isActive}
                    className="relative z-10 h-4 w-4 shrink-0 rounded-full border-2 border-white shadow-sm transition-transform hover:scale-125 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:border-navy-800"
                    style={{ background: color }}
                  />
                  {!isLast && (
                    <div className="h-px flex-1 bg-gray-200 dark:bg-white/10" />
                  )}
                </div>

                <p className="mt-1 text-center text-[9px] leading-tight text-gray-400">
                  {fmt(camp.date, { month: "short", year: "2-digit" })}
                </p>
              </div>
            );
          })}
        </div>
      </div>

      {activeCamp && (
        <div className="mt-3 rounded-lg border border-gray-200 bg-white px-3 py-2.5 shadow-sm dark:border-white/10 dark:bg-navy-900">
          <p className="mb-1.5 text-[10px] font-bold text-gray-400">
            {activeCamp.id} ·{" "}
            {fmt(activeCamp.date, {
              day: "numeric",
              month: "long",
              year: "numeric",
            })}
          </p>
          {[
            { label: "Fused", level: activeCamp.fused_level },
            { label: "CTAG", level: activeCamp.ctag_level },
            { label: "Physical", level: activeCamp.physical_level },
          ].map(({ label, level }) => {
            const c = ISAD_CONFIG[level];
            return (
              <div
                key={label}
                className="flex items-center justify-between py-0.5"
              >
                <span className="text-xs text-gray-500">{label}</span>
                <span
                  className="text-xs font-bold"
                  style={{ color: c?.color ?? "#9e9e9e" }}
                >
                  {level} — {c?.label ?? "No data"}
                </span>
              </div>
            );
          })}
        </div>
      )}
      {oscillation && (
        <div className="mt-3">
          <p className="mb-2 text-[10px] font-semibold text-gray-400">
            Reversal detected — campaign comparison
          </p>
          <div className="flex items-center gap-2">
            <CampaignCard camp={oscillation.before} label="Before" />
            <span className="shrink-0 text-gray-300 dark:text-gray-600">→</span>
            <CampaignCard camp={oscillation.after} label="After" />
          </div>
        </div>
      )}
    </section>
  );
};

export default WhatChanged;
